import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { juntarClasses } from './classes';
import { IconeAlerta } from './icones';

/**
 * Campos de formulário.
 *
 * Rótulo, ajuda e erro são props obrigatórias do desenho, não enfeite: o legado
 * mostrava validação como texto solto ao lado do input, sem `aria-describedby`
 * nem `aria-invalid`, e quem navegava por teclado ou leitor de tela não ficava
 * sabendo por que o formulário não enviava (RN-621, RN-639).
 *
 * O anel de foco fica no envoltório, via `has-[:focus-visible]`, para o input
 * com prefixo (`R$`) ou sufixo (`%`) acender inteiro — o controle interno
 * cancela o anel global com `focus-visible:ring-0`.
 */

const ENVOLTORIO_BASE =
  'flex w-full items-center gap-2 rounded-card border bg-superficie-elevada px-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-acento-ciano has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-superficie-base';

const CONTROLE_BASE =
  'w-full min-w-0 bg-transparent text-sm text-conteudo-primario placeholder:text-conteudo-sutil focus:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:text-conteudo-sutil';

const AFIXO = 'shrink-0 text-xs text-conteudo-secundario';

function classesDoEnvoltorio(invalido: boolean, desabilitado: boolean, extra?: string): string {
  return juntarClasses(
    ENVOLTORIO_BASE,
    invalido ? 'border-erro' : 'border-borda hover:border-acento-ciano/40',
    desabilitado && 'cursor-not-allowed opacity-60 hover:border-borda',
    extra,
  );
}

interface IdsDeCampo {
  id: string;
  idAjuda: string;
  idErro: string;
  descritoPor: string | undefined;
}

function useIdsDeCampo(idInformado: string | undefined, ajuda?: string, erro?: string): IdsDeCampo {
  const gerado = useId();
  const id = idInformado ?? `campo${gerado}`;
  const idAjuda = `${id}-ajuda`;
  const idErro = `${id}-erro`;
  const descricoes: string[] = [];
  if (ajuda !== undefined && ajuda !== '') descricoes.push(idAjuda);
  if (erro !== undefined && erro !== '') descricoes.push(idErro);
  return {
    id,
    idAjuda,
    idErro,
    descritoPor: descricoes.length > 0 ? descricoes.join(' ') : undefined,
  };
}

interface PropsComuns {
  rotulo: string;
  /** Mantém o rótulo só para leitor de tela — use quando o contexto já o diz. */
  rotuloOculto?: boolean;
  obrigatorio?: boolean;
  ajuda?: string;
  /** Presente ⇒ campo inválido. Vira `aria-invalid` + `aria-describedby`. */
  erro?: string;
  /** Só para layout. */
  className?: string;
}

interface PropsDaMoldura extends PropsComuns, IdsDeCampo {
  children: ReactNode;
}

/** Rótulo, ajuda e erro em volta de qualquer controle. */
function Moldura({
  rotulo,
  rotuloOculto = false,
  obrigatorio = false,
  ajuda,
  erro,
  className,
  id,
  idAjuda,
  idErro,
  children,
}: PropsDaMoldura): JSX.Element {
  return (
    <div className={juntarClasses('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className={juntarClasses(
          'text-sm font-medium text-conteudo-secundario',
          rotuloOculto && 'sr-only',
        )}
      >
        {rotulo}
        {obrigatorio && (
          <>
            <span aria-hidden="true" className="ml-1 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </label>

      {children}

      {ajuda !== undefined && ajuda !== '' && (
        <p id={idAjuda} className="text-xs text-conteudo-secundario">
          {ajuda}
        </p>
      )}

      {erro !== undefined && erro !== '' && (
        <p
          id={idErro}
          // `polite` em vez de `alert`: a mensagem já é anunciada pelo
          // `aria-describedby` ao focar. O live region cobre o caso da
          // validação que chega depois do submit, sem interromper a digitação.
          aria-live="polite"
          className="flex items-start gap-1.5 text-xs text-erro"
        >
          <IconeAlerta className="mt-px h-3.5 w-3.5" />
          <span>{erro}</span>
        </p>
      )}
    </div>
  );
}

export interface PropsCampo
  extends PropsComuns, Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'children'> {
  /** Texto ou ícone colado à esquerda do valor — `R$`, `CNPJ`. */
  prefixo?: ReactNode;
  /** Texto ou ícone colado à direita — `%`, `/ mês`. */
  sufixo?: ReactNode;
  /**
   * Valor fiscal: alinha à direita, aplica números tabulares e abre o teclado
   * numérico no celular.
   */
  numerico?: boolean;
  /** Na altura dos botões pequenos (h-9): para ficar numa barra de ações ao lado deles. */
  compacto?: boolean;
}

export const Campo = forwardRef<HTMLInputElement, PropsCampo>(function Campo(
  {
    rotulo,
    rotuloOculto,
    obrigatorio,
    ajuda,
    erro,
    className,
    prefixo,
    sufixo,
    numerico = false,
    compacto = false,
    id: idInformado,
    disabled,
    ...resto
  },
  referencia,
): JSX.Element {
  const ids = useIdsDeCampo(idInformado, ajuda, erro);
  const invalido = erro !== undefined && erro !== '';

  return (
    <Moldura
      rotulo={rotulo}
      rotuloOculto={rotuloOculto}
      obrigatorio={obrigatorio}
      ajuda={ajuda}
      erro={erro}
      className={className}
      {...ids}
    >
      <div className={classesDoEnvoltorio(invalido, disabled === true, compacto ? 'h-9' : 'h-11')}>
        {prefixo !== undefined && <span className={AFIXO}>{prefixo}</span>}
        <input
          ref={referencia}
          id={ids.id}
          disabled={disabled}
          aria-invalid={invalido || undefined}
          aria-describedby={ids.descritoPor}
          aria-required={obrigatorio || undefined}
          inputMode={numerico ? 'decimal' : undefined}
          className={juntarClasses(CONTROLE_BASE, numerico && 'valor-fiscal text-right')}
          {...resto}
        />
        {sufixo !== undefined && <span className={AFIXO}>{sufixo}</span>}
      </div>
    </Moldura>
  );
});

export interface PropsCampoDeTexto
  extends
    PropsComuns,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'children'> {}

export const CampoDeTexto = forwardRef<HTMLTextAreaElement, PropsCampoDeTexto>(
  function CampoDeTexto(
    {
      rotulo,
      rotuloOculto,
      obrigatorio,
      ajuda,
      erro,
      className,
      id: idInformado,
      disabled,
      rows = 4,
      ...resto
    },
    referencia,
  ): JSX.Element {
    const ids = useIdsDeCampo(idInformado, ajuda, erro);
    const invalido = erro !== undefined && erro !== '';

    return (
      <Moldura
        rotulo={rotulo}
        rotuloOculto={rotuloOculto}
        obrigatorio={obrigatorio}
        ajuda={ajuda}
        erro={erro}
        className={className}
        {...ids}
      >
        <div className={classesDoEnvoltorio(invalido, disabled === true, 'py-2')}>
          <textarea
            ref={referencia}
            id={ids.id}
            rows={rows}
            disabled={disabled}
            aria-invalid={invalido || undefined}
            aria-describedby={ids.descritoPor}
            aria-required={obrigatorio || undefined}
            className={juntarClasses(CONTROLE_BASE, 'resize-y leading-relaxed')}
            {...resto}
          />
        </div>
      </Moldura>
    );
  },
);

export interface OpcaoDeSelecao {
  valor: string;
  rotulo: string;
  desabilitada?: boolean;
}

export interface PropsCampoDeSelecao
  extends PropsComuns, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'children'> {
  opcoes: readonly OpcaoDeSelecao[];
  /** Primeira opção neutra, sem valor — some assim que algo é escolhido. */
  placeholder?: string;
}

export const CampoDeSelecao = forwardRef<HTMLSelectElement, PropsCampoDeSelecao>(
  function CampoDeSelecao(
    {
      rotulo,
      rotuloOculto,
      obrigatorio,
      ajuda,
      erro,
      className,
      opcoes,
      placeholder,
      id: idInformado,
      disabled,
      ...resto
    },
    referencia,
  ): JSX.Element {
    const ids = useIdsDeCampo(idInformado, ajuda, erro);
    const invalido = erro !== undefined && erro !== '';

    return (
      <Moldura
        rotulo={rotulo}
        rotuloOculto={rotuloOculto}
        obrigatorio={obrigatorio}
        ajuda={ajuda}
        erro={erro}
        className={className}
        {...ids}
      >
        {/*
          A seta fica **por cima** do `select`, não ao lado dele.

          Como irmã num flex, ela ocupava um pedaço da caixa que o `select` não
          cobria: clicar bem na seta — o lugar mais óbvio para abrir a lista —
          não abria nada, porque o clique caía no envoltório. Agora o `select`
          ocupa a caixa inteira (o recuo saiu do envoltório e foi para dentro
          dele) e a seta é absoluta e sem eventos: qualquer ponto da caixa,
          inclusive a seta, abre a lista.
        */}
        <div className={classesDoEnvoltorio(invalido, disabled === true, 'relative h-11 px-0')}>
          <select
            ref={referencia}
            id={ids.id}
            disabled={disabled}
            aria-invalid={invalido || undefined}
            aria-describedby={ids.descritoPor}
            aria-required={obrigatorio || undefined}
            className={juntarClasses(CONTROLE_BASE, 'h-full appearance-none pl-3 pr-9')}
            {...resto}
          >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {opcoes.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor} disabled={opcao.desabilitada}>
                {opcao.rotulo}
              </option>
            ))}
          </select>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-conteudo-secundario"
          >
            <path d="m7 10 5 5 5-5" />
          </svg>
        </div>
      </Moldura>
    );
  },
);
